import React, { type JSX } from 'react';
import ReactDOM from 'react-dom/client';
import * as Sentry from '@sentry/react';
import posthog from 'posthog-js';
import { PostHogProvider } from 'posthog-js/react';
import App from './App';
import './index.css';

// arbi-dev shares Sentry + PostHog projects with arbi (single project per
// observability tool). Events are distinguished by the `environment` tag
// (Sentry) and the `environment` super-property (PostHog). Filter for
// 'arbi-dev' (or 'arbi-dev-local') in either dashboard to scope to this app.
//
// Day 63b Gap 1 fix: environment is mode-conditional so local dev runs
// (npm run dev:arbi-dev on localhost:5174) emit events tagged
// 'arbi-dev-local', distinguishable from prod 'arbi-dev' events. Without
// this, the Day 63b Error Log Viewer can't filter local-dev noise out
// of the prod error stream. Same shape as arbi-root's
// `environment: import.meta.env.MODE` pattern (which gives 'development'
// vs 'production'); arbi-dev needs the literal-prefix variant because
// both modes target the same Sentry project as the same tag domain.
const SENTRY_ENV = import.meta.env.PROD ? 'arbi-dev' : 'arbi-dev-local';

Sentry.init({
  dsn: import.meta.env.VITE_SENTRY_DSN,
  environment: SENTRY_ENV,
  release: import.meta.env.VITE_VERCEL_GIT_COMMIT_SHA,
  enabled: import.meta.env.PROD && Boolean(import.meta.env.VITE_SENTRY_DSN),
});

if (import.meta.env.PROD && import.meta.env.VITE_POSTHOG_KEY) {
  posthog.init(import.meta.env.VITE_POSTHOG_KEY, {
    api_host: import.meta.env.VITE_POSTHOG_HOST || 'https://us.i.posthog.com',
    capture_pageview: 'history_change',
    person_profiles: 'identified_only',
    autocapture: true,
    loaded: (ph) => {
      ph.register({ environment: SENTRY_ENV });
      if (import.meta.env.MODE === 'development') ph.debug();
    },
  });
}

// main.tsx is the entry point — it has no exports by design; the local
// FallbackUI is only ever referenced from the createRoot call below.
// eslint-disable-next-line react-refresh/only-export-components
function FallbackUI(): JSX.Element {
  return (
    <div className="min-h-screen bg-surface-0 flex items-center justify-center p-8">
      <div className="max-w-md text-center space-y-3">
        <div className="text-ink-bright text-lg font-semibold">Something went wrong</div>
        <div className="text-ink-secondary text-sm">
          The error has been logged. Try refreshing the page — if the problem persists, contact an
          admin.
        </div>
      </div>
    </div>
  );
}

const rootEl = document.getElementById('root');
if (!rootEl) throw new Error('Root element #root not found');

ReactDOM.createRoot(rootEl).render(
  <React.StrictMode>
    <PostHogProvider client={posthog}>
      <Sentry.ErrorBoundary fallback={<FallbackUI />}>
        <App />
      </Sentry.ErrorBoundary>
    </PostHogProvider>
  </React.StrictMode>
);
